// ── OrgAdminsPage.js ──────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from "react";
import { orgAdmin, superAdmin } from "../../api";
import { useAuth } from "../../context/AuthContext";
import {
  PageHeader, Btn, Badge, EmptyState, LoadingSpinner,
  FormField, inputStyle, TempPasswordBox,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

export function OrgAdminsPage() {
  const { user }  = useAuth();
  const orgId     = user?.organisationId;
  const isSuperAdmin = user?.role === "super_admin";

  const [admins,  setAdmins]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [result,  setResult]  = useState(null);

  const load = useCallback(() => {
    if (!orgId) return;
    setLoading(true);
    (isSuperAdmin ? superAdmin.listUsers({ role: "org_admin" }) : orgAdmin.getAdmins(orgId))
      .then(r => setAdmins(r.data?.results || r.data || []))
      .catch(() => toast.error("Failed to load org admins."))
      .finally(() => setLoading(false));
  }, [orgId, isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "800px" }}>
      <PageHeader
        title="Organisation Admins"
        subtitle="Only Super Admin can add org admins. Multiple org admins are supported."
        action={isSuperAdmin && <Btn onClick={() => setShowAdd(true)}>+ Add Org Admin</Btn>}
      />

      {!isSuperAdmin && (
        <div style={{ background: "rgba(37,99,235,0.07)", borderRadius: "10px", padding: "14px 18px", marginBottom: "24px", fontSize: "13px", color: "#64748B" }}>
          ℹ️ Contact your Super Admin to add or remove organisation admins.
        </div>
      )}

      {loading ? <LoadingSpinner /> : admins.length === 0 ? (
        <EmptyState icon="👥" title="No org admins yet" subtitle={isSuperAdmin ? "Add the first org admin for this organisation." : "Contact Super Admin to add org admins."} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {admins.map(a => (
            <div key={a.id} style={{ background: "rgba(15,23,42,0.6)", border: "1px solid rgba(37,99,235,0.15)", borderRadius: "12px", padding: "16px 20px", display: "flex", alignItems: "center", gap: "14px" }}>
              <div style={{ width: "36px", height: "36px", borderRadius: "9px", background: "linear-gradient(135deg,#2563EB,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", fontWeight: "700", color: "#fff", flexShrink: 0 }}>
                {a.name?.[0]?.toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "14px", fontWeight: "600", color: "#E2E8F0" }}>{a.name}</div>
                <div style={{ fontSize: "12px", color: "#475569" }}>{a.email}</div>
              </div>
              <Badge value={a.is_active ? "active" : "inactive"} />
            </div>
          ))}
        </div>
      )}

      {/* Add org admin modal — super admin only */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Org Admin" width="440px">
        <AddOrgAdminForm
          orgId={orgId}
          onClose={() => setShowAdd(false)}
          onAdded={(data) => { setShowAdd(false); setResult(data); load(); }}
        />
      </Modal>

      <Modal open={!!result} onClose={() => setResult(null)} title="Org Admin Added" width="440px">
        {result && (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <TempPasswordBox password={result.temp_password} email={result.admin?.email} />
            <div style={{ display: "flex", justifyContent: "flex-end" }}><Btn onClick={() => setResult(null)}>Done</Btn></div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function AddOrgAdminForm({ orgId, onClose, onAdded }) {
  const [form, setForm]   = useState({ name: "", email: "", password: "", send_invite: true });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required."); return; }
    setSaving(true);
    try {
      const { data } = await superAdmin.addOrgAdmin
        ? superAdmin.addOrgAdmin(orgId, form)
        : orgAdmin.addAdmin(orgId, form);
      toast.success(`${form.name} added as org admin.`);
      onAdded(data);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to add org admin.");
    } finally { setSaving(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <FormField label="Full Name" required><input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Kofi Ansa" /></FormField>
      <FormField label="Email" required><input style={inputStyle} type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="admin@organisation.com" /></FormField>
      <FormField label="Password" hint="Leave blank to auto-generate"><input style={inputStyle} type="password" value={form.password} onChange={e => set("password", e.target.value)} placeholder="Min. 8 chars or leave blank" /></FormField>
      <label style={{ display: "flex", gap: "8px", cursor: "pointer", fontSize: "13px", color: "#94A3B8" }}>
        <input type="checkbox" checked={form.send_invite} onChange={e => set("send_invite", e.target.checked)} />
        Send welcome email
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid rgba(37,99,235,0.1)" }}>
        <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving || !form.name.trim() || !form.email.trim()}>{saving ? "Adding…" : "Add Org Admin"}</Btn>
      </div>
    </div>
  );
}


// ── OrgActivityPage.js ────────────────────────────────────────────────────────
export function OrgActivityPage() {
  const { user }  = useAuth();
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    facilityAdmin_activityLog()
      .then(r => setLogs(r.data?.results || r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    // Mark all as read when admin opens this page
    import("../../api").then(({ facilityAdmin }) => facilityAdmin.markLogsRead().catch(() => {}));
  }, []);

  const ACTION_LABELS = {
    settings_changed: "⚙️ Settings Changed",
    staff_added:      "👤 Staff Added",
    staff_edited:     "✏️ Staff Edited",
    staff_deactivated:"🚫 Staff Deactivated",
    zone_modified:    "🗂️ Zone Modified",
    station_modified: "📍 Station Modified",
    counter_modified: "🔢 Counter Modified",
    outcome_modified: "↩️ Outcome Modified",
    audio_changed:    "🔊 Audio Changed",
    branch_suspended: "⛔ Branch Suspended",
    branch_activated: "✅ Branch Activated",
  };

  const filtered = filter === "all" ? logs : logs.filter(l => l.action_type === filter);

  return (
    <div style={{ padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "900px" }}>
      <PageHeader
        title="Activity Log"
        subtitle="Changes made by your organisation admins to this branch."
      />

      <div style={{ marginBottom: "20px" }}>
        <select
          style={{ background: "rgba(30,41,59,0.8)", border: "1px solid rgba(51,65,85,0.8)", borderRadius: "8px", padding: "8px 12px", color: "#F1F5F9", fontSize: "13px", outline: "none", fontFamily: "inherit" }}
          value={filter} onChange={e => setFilter(e.target.value)}
        >
          <option value="all">All actions</option>
          {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {loading ? <LoadingSpinner /> : filtered.length === 0 ? (
        <EmptyState icon="🔔" title="No activity yet" subtitle="Changes made by org admin will appear here." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {filtered.map(log => (
            <div key={log.id} style={{
              background: log.is_read ? "rgba(15,23,42,0.5)" : "rgba(37,99,235,0.08)",
              border: `1px solid ${log.is_read ? "rgba(37,99,235,0.1)" : "rgba(37,99,235,0.3)"}`,
              borderRadius: "12px", padding: "14px 18px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "13px", fontWeight: "600", color: "#E2E8F0", marginBottom: "4px" }}>
                    {ACTION_LABELS[log.action_type] || log.action_type}
                    {!log.is_read && <span style={{ marginLeft: "8px", background: "#2563EB", color: "#fff", borderRadius: "4px", padding: "1px 6px", fontSize: "10px", fontWeight: "700" }}>NEW</span>}
                  </div>
                  <div style={{ fontSize: "13px", color: "#94A3B8" }}>{log.description}</div>
                  <div style={{ fontSize: "11px", color: "#475569", marginTop: "6px" }}>
                    By {log.performed_by_name} · {new Date(log.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Lazy import helper for activity log
function facilityAdmin_activityLog() {
  return import("../../api").then(({ facilityAdmin }) => facilityAdmin.activityLog());
}
